import { ProductReview } from "@/components/molecules/ProductReview/ProductReview"
import { HomeProductSection } from "@/components/sections/HomeProductSection/HomeProductSection"
import { BarebonesProductBuyBox } from "@/components/skins/barebones/BarebonesProductBuyBox"
import { BarebonesProductGallery } from "@/components/skins/barebones/BarebonesProductGallery"
import { BarebonesProductStory } from "@/components/skins/barebones/BarebonesProductStory"
import { retrieveCustomer } from "@/lib/data/customer"
import { listSellerProducts } from "@/lib/data/products"
import { getUserWishlists } from "@/lib/data/wishlist"
import { buildProductStorySections } from "@/lib/helpers/build-product-story-sections"
import { ensureProductDetailContent } from "@/lib/helpers/ensure-product-detail-content"
import { SellerProps } from "@/types/seller"
import { Wishlist } from "@/types/wishlist"
import { HttpTypes } from "@medusajs/types"
import { getTranslate } from "@/tolgee/server"
import { Suspense } from "react"

async function SuggestedProducts({
  sellerHandle,
  locale,
}: {
  sellerHandle: string
  locale: string
}) {
  const t = await getTranslate()
  const sellerProducts = await listSellerProducts({
    handle: sellerHandle,
    query: {
      limit: 12,
      fields: "*",
    },
    countryCode: locale,
  })

  return (
    <HomeProductSection
      heading={t("barebones_pdp.suggested", "Suggested Products")}
      products={sellerProducts.products}
      seller_handle={sellerHandle}
      locale={locale}
    />
  )
}

export async function BarebonesProductDetailsPage({
  product: rawProduct,
  locale,
}: {
  product: HttpTypes.StoreProduct & {
    seller?: SellerProps
  }
  locale: string
}) {
  const product = ensureProductDetailContent(rawProduct)
  const user = await retrieveCustomer()
  let wishlist: Wishlist[] = []
  if (user) {
    const response = await getUserWishlists().catch(() => ({
      wishlists: [] as Wishlist[],
    }))
    wishlist = response.wishlists
  }

  const galleryImages =
    product.images?.length
      ? product.images
      : product.thumbnail
        ? [
            {
              id: "thumbnail",
              url: product.thumbnail,
              rank: 0,
            } as NonNullable<HttpTypes.StoreProduct["images"]>[number],
          ]
        : []

  const t = await getTranslate()
  const storySections = buildProductStorySections(product).map((section, i) => ({
    ...section,
    eyebrow: [
      t("barebones_pdp.materials", "Materials"),
      t("barebones_pdp.features", "Features"),
      t("barebones_pdp.design", "Design"),
    ][i] || section.eyebrow,
  }))
  const sellerHandle = product.seller?.handle

  return (
    <div className="bb-pdp w-full bg-[#f2f0eb] text-[#2b2118]">
      <div className="mx-auto grid w-full max-w-[1440px] gap-10 px-8 py-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14 lg:py-16">
        <BarebonesProductGallery images={galleryImages} />
        <BarebonesProductBuyBox
          product={product}
          locale={locale}
          user={user}
          wishlist={wishlist}
        />
      </div>

      <BarebonesProductStory sections={storySections} />

      {sellerHandle ? (
        <div className="mx-auto w-full max-w-[1440px] px-8 py-16">
          <Suspense
            fallback={
              <div className="h-64 w-full animate-pulse bg-[#e4ddd2]/60" />
            }
          >
            <SuggestedProducts sellerHandle={sellerHandle} locale={locale} />
          </Suspense>
        </div>
      ) : null}

      <div className="mx-auto w-full max-w-[1440px] px-8 pb-16">
        <ProductReview product_id={product.id} locale={locale} />
      </div>
    </div>
  )
}
